import {
  CHAT_STATUS_FILTER,
  FEATURES,
  GROUP_TYPE,
  ICounterData,
  SERVICE_STATUS,
} from "@home-health-notify/domain";
import CampaignIcon from "@mui/icons-material/Campaign";
import GroupsIcon from "@mui/icons-material/Groups";
import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Divider } from "@mui/material";
import Badge from "@mui/material/Badge";
import {
  BroadcastChatsProvider,
  FilteredHiddenChatsProvider,
  FilteredInternalChatsProvider,
  FilteredPatientChatsProvider,
  FilteredPrivateChatsProvider,
  HiddenChatsProvider,
  InternalChatsProvider,
  OrganizationContext,
  PatientChatsProvider,
  PrivateChatsProvider,
  useTranslation,
} from "hhn-commons";
import * as React from "react";
import { useContext, useEffect } from "react";
import { useParams } from "react-router";
import TabPanel from "../common/tabs/TabPanel";
import { ROUTES } from "../routes";
import {
  ChatListTabItem,
  MenuBarTabsContainer,
  MessagePanelBox,
  MessagePanelContainer,
  MessagePanelMenuBar,
} from "./ChatListPanel.style";
import BroadcastChats from "./group/broadcast/BroadcastChats";
import InternalChats from "./group/internal/InternalChats";
import { PatientChats } from "./group/patient/PatientChats";
import HiddenGroups from "./hiddenGroups/HiddenGroups";
import { PrivateChats } from "./private/PrivateChats";

export interface FilterDataType {
  filterBy: CHAT_STATUS_FILTER;
  patientStatus?: number[];
  servicesToPatient?: {
    id: string;
    status: SERVICE_STATUS;
  };
}

export const ChatListPanel = ({ counters }: { counters: ICounterData }) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "chat" });
  const { selectedOrgUser } = useContext(OrganizationContext);

  const allTabs = [
    {
      index: 0,
      icon: <HomeIcon />,
      label: tNs("patient"),
      path: ROUTES.PATIENT,
      access: true,
      groupType: GROUP_TYPE.PATIENT,
      invisible: counters.message[GROUP_TYPE.PATIENT] <= 0,
      dataTestId: "patient-chats-tab",
    },
    {
      index: 1,
      icon: <GroupsIcon />,
      label: tNs("internalGroups"),
      path: ROUTES.INTERNAL,
      access: selectedOrgUser?.hasAccess(FEATURES.VIEW_INTERNAL_GROUPS),
      groupType: GROUP_TYPE.NON_PATIENT,
      invisible: counters.message[GROUP_TYPE.NON_PATIENT] <= 0,
      dataTestId: "internal-chats-tab",
    },
    {
      index: 2,
      icon: <PersonIcon />,
      label: tNs("direct"),
      path: ROUTES.PRIVATE,
      access: true,
      groupType: GROUP_TYPE.PRIVATE,
      invisible: counters.message[GROUP_TYPE.PRIVATE] <= 0,
      dataTestId: "direct-messages-tab",
    },
    {
      index: 3,
      icon: <CampaignIcon />,
      label: tNs("broadcasts"),
      path: ROUTES.BROADCAST,
      access: selectedOrgUser?.hasAccess(FEATURES.VIEW_BROADCAST),
      groupType: GROUP_TYPE.BROADCAST,
      invisible: true,
      dataTestId: "broadcast-tab",
    },
    {
      index: 4,
      icon: <VisibilityOffIcon />,
      label: tNs("hiddenGroups"),
      path: "",
      access: selectedOrgUser?.hasAccess(FEATURES.VIEW_HIDDEN_GROUPS),
      invisible: counters.message[GROUP_TYPE.HIDDEN] <= 0,
      dataTestId: "hidden-chats-tab",
    },
  ];

  const params = useParams();
  const pathArr = params["*"]?.split("/");
  const currentPath = pathArr && pathArr[1];
  let chatID = undefined;
  if (pathArr && pathArr.length >= 3 && pathArr[2]) chatID = pathArr[2];

  const getActiveTabValue = () => {
    const currentTab = currentPath
      ? allTabs.find(tab => currentPath === tab.path)
      : undefined;
    return currentTab ? currentTab.index : allTabs[0].index;
  };
  const [tabValue, setTabValue] = React.useState(getActiveTabValue());
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  //Switch back to first tab when org change
  useEffect(() => {
    setTabValue(getActiveTabValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.orgId]);

  return (
    <MessagePanelContainer>
      <MessagePanelBox>
        <MessagePanelMenuBar>
          <MenuBarTabsContainer
            slotProps={{
              indicator: { style: { display: "none" } },
            }}
            value={tabValue}
            onChange={handleChange}
            aria-label="tabs in left panel"
            variant="fullWidth"
          >
            {allTabs
              .filter(tab => tab.access)
              .map((tab, index) => (
                <ChatListTabItem
                  icon={
                    <Badge
                      sx={{ marginY: "6px" }}
                      variant="dot"
                      badgeContent={index === 3 ? 0 : null}
                      color="primary"
                      invisible={tab.invisible}
                      data-testid={`${tab.dataTestId}-unread-badge`}
                    >
                      {tab.icon}
                    </Badge>
                  }
                  key={index}
                  label={tab.label}
                  data-testid={tab.dataTestId}
                />
              ))}
          </MenuBarTabsContainer>
        </MessagePanelMenuBar>
      </MessagePanelBox>
      <Divider />
      <TabPanel value={tabValue} index={0} uniqueKey="chat-list">
        <PatientChatsProvider>
          <FilteredPatientChatsProvider>
            <PatientChats
              selectedChatId={chatID}
              unreadChatCount={counters.message[GROUP_TYPE.PATIENT]}
            />
          </FilteredPatientChatsProvider>
        </PatientChatsProvider>
      </TabPanel>
      {selectedOrgUser?.hasAccess(FEATURES.VIEW_INTERNAL_GROUPS) && (
        <TabPanel value={tabValue} index={1} uniqueKey="chat-list">
          <InternalChatsProvider>
            <FilteredInternalChatsProvider>
              <InternalChats
                selectedChatId={chatID}
                unreadChatCount={counters.message[GROUP_TYPE.NON_PATIENT]}
              />
            </FilteredInternalChatsProvider>
          </InternalChatsProvider>
        </TabPanel>
      )}
      <TabPanel value={tabValue} index={2} uniqueKey="chat-list">
        <PrivateChatsProvider>
          <FilteredPrivateChatsProvider>
            <PrivateChats
              selectedChatId={chatID}
              unreadChatCount={counters.message[GROUP_TYPE.PRIVATE]}
            />
          </FilteredPrivateChatsProvider>
        </PrivateChatsProvider>
      </TabPanel>
      {selectedOrgUser?.hasAccess(FEATURES.VIEW_BROADCAST) && (
        <TabPanel value={tabValue} index={3} uniqueKey="chat-list">
          <BroadcastChatsProvider>
            <BroadcastChats selectedChatId={chatID} />
          </BroadcastChatsProvider>
        </TabPanel>
      )}
      {selectedOrgUser?.hasAccess(FEATURES.VIEW_HIDDEN_GROUPS) && (
        <TabPanel value={tabValue} index={4} uniqueKey="chat-list">
          <HiddenChatsProvider>
            <FilteredHiddenChatsProvider>
              <HiddenGroups
                selectedChatId={chatID}
                unreadChatCount={counters.message[GROUP_TYPE.HIDDEN]}
              />
            </FilteredHiddenChatsProvider>
          </HiddenChatsProvider>
        </TabPanel>
      )}
    </MessagePanelContainer>
  );
};
